import ModalWrapper from '@app/common/ModalWrapper';
import { EnvelopeDirection, Modal } from '@app/const/consts';
import { useAppDispatch, useAppSelector } from '@app/hooks/redux';
import { resetModal } from '@app/store/modules/modal';
import { IEnvelope } from '@app/store/modules/envelopes/types';
import { IParams } from '@app/types';
import {
  EnvelopeFieldType,
  IEnvelopeField,
} from '@store/modules/boxes/types';
import EnvelopesService, {
  EnvelopeZipExportJobStatus,
  EnvelopeZipExportMode,
  EnvelopeZipExportNamePrefixMode,
  IEnvelopeZipExportJobPostRequest,
  IEnvelopeZipExportJobResponse,
} from '@services/envelopes';
import {
  Button,
  FormLabel,
  IconCheck,
  IconInput,
  IconWarning,
  ILibModalSizes,
  Input,
  Spinner,
} from '@infologistics/frontend-libraries';
import React, { ChangeEvent, FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import {
  getEnvelopeExportTargets,
  IEnvelopeExportTarget,
} from '../../EnvelopesHead/excelExport';

import styles from './EnvelopeMassDownloadModal.module.scss';

enum DownloadStatus {
  QUEUED = 'queued',
  PROCESSING = 'processing',
  DONE = 'done',
  ERROR = 'error',
}

interface IEnvelopeMassDownloadModalProps {
  boxOguid: string;
  currentDirection: EnvelopeDirection | null;
  envelopeFields: IEnvelopeField[];
  filterParams: IParams;
  formatLocale: string;
  isAllEnvelopesSelected: boolean;
  isMyTasks: boolean;
  selectedEnvelopes: IEnvelope[];
}

interface IDownloadRow {
  error?: string;
  exportedEnvelopeCount?: number | null;
  fileName?: string;
  status: DownloadStatus;
  title: string;
}

interface ITokenOption {
  key: string;
  label: string;
}

const DEFAULT_FILENAME_MASK = '{%fileName%}';
const ZIP_ROW_KEY = 'zip-export';
const ZIP_POLL_INTERVAL_MS = 2000;
const ZIP_POLL_ATTEMPTS = 300;

interface IContinuationState {
  direction: EnvelopeDirection;
  jobIndex: number;
  previousJobOguid: string;
}

interface IExportAttempt {
  direction: EnvelopeDirection;
  envelopeCount?: number;
  jobIndex: number;
  params: IParams;
  request: IEnvelopeZipExportJobPostRequest;
}

const systemTokenKeys = [
  'subject',
  'envelopeOguid',
  'senderEmail',
  'senderFullName',
  'fileName',
];

const terminalStatuses = new Set<EnvelopeZipExportJobStatus>([
  EnvelopeZipExportJobStatus.COMPLETED,
  EnvelopeZipExportJobStatus.ERROR,
  EnvelopeZipExportJobStatus.STOPPED,
  EnvelopeZipExportJobStatus.EXPIRED,
]);

const getErrorMessage = (err: any, fallback: string): string =>
  err.response?.data?.message || err.message || fallback;

const sleep = async (delay: number): Promise<void> =>
  new Promise((resolve) => window.setTimeout(resolve, delay));

const getContentDispositionFileName = (contentDisposition?: string): string | null => {
  if (!contentDisposition) return null;

  const encodedMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (encodedMatch?.[1]) {
    try {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    } catch {
      return encodedMatch[1].trim().replace(/^"|"$/g, '');
    }
  }

  const fallbackMatch = contentDisposition.match(/filename="?([^";]+)"?/i);

  return fallbackMatch?.[1] ?? null;
};

const getContentDispositionHeader = (headers: Record<string, string | undefined>): string | undefined =>
  headers['content-disposition'] ?? headers['Content-Disposition'];

const normalizeNameRulesTemplate = (template: string, fieldKeys: Set<string>): string => {
  const normalizedTemplate = template.trim() || DEFAULT_FILENAME_MASK;

  return normalizedTemplate.replace(
    /\{%\s*([^{}%]+?)\s*%\}|\{\s*([^{}]+?)\s*\}/g,
    (match: string, percentKey?: string, legacyKey?: string): string => {
      const key = (percentKey ?? legacyKey ?? '').trim();

      if (!key) return match;
      if (key.startsWith('field:')) return `{%${key}%}`;
      if (fieldKeys.has(key)) return `{%field:${key}%}`;

      return `{%${key}%}`;
    }
  );
};

const getJobProgressLabel = (
  job: IEnvelopeZipExportJobResponse,
  fallback: string
): string => {
  if (typeof job.fileCount === 'number' && typeof job.processedFileCount === 'number') {
    return `${job.processedFileCount} / ${job.fileCount}`;
  }

  if (typeof job.fileCount === 'number') {
    return `0 / ${job.fileCount}`;
  }

  return fallback;
};

export const EnvelopeMassDownloadModal: FC<IEnvelopeMassDownloadModalProps> = ({
  boxOguid,
  currentDirection,
  envelopeFields,
  filterParams,
  formatLocale,
  isAllEnvelopesSelected,
  isMyTasks,
  selectedEnvelopes,
}) => {
  const { t } = useTranslation(['common', 'envelopes']);
  const dispatch = useAppDispatch();
  const activeModal = useAppSelector((state) => state.modal.modal);
  const maskInputRef = useRef<HTMLInputElement>(null);
  const [fileNameMask, setFileNameMask] = useState(DEFAULT_FILENAME_MASK);
  const [isTokenBuilderOpen, setIsTokenBuilderOpen] = useState(false);
  const [rows, setRows] = useState<Record<string, IDownloadRow>>({});
  const [processing, setProcessing] = useState(false);
  const [processed, setProcessed] = useState(false);
  const [globalError, setGlobalError] = useState('');
  const [continuationStates, setContinuationStates] = useState<IContinuationState[]>([]);

  const isOpen = activeModal === Modal.ENVELOPE_MASS_DOWNLOAD;

  const resetDialogState = useCallback((): void => {
    setFileNameMask(DEFAULT_FILENAME_MASK);
    setIsTokenBuilderOpen(false);
    setRows({});
    setProcessing(false);
    setProcessed(false);
    setGlobalError('');
    setContinuationStates([]);
  }, []);

  useEffect(() => {
    if (!isOpen) resetDialogState();
  }, [isOpen, resetDialogState]);

  const fieldKeys = useMemo(
    () => new Set(envelopeFields.map(({ systemKey }) => systemKey)),
    [envelopeFields]
  );

  const tokenOptions = useMemo<ITokenOption[]>(
    () => [
      ...systemTokenKeys.map((key) => ({
        key,
        label: t(`envelopes:massDownload.tokens.${key}`),
      })),
      ...envelopeFields
        .filter(({ type }) => type !== EnvelopeFieldType.TABLE)
        .map((field) => ({
          key: `field:${field.systemKey}`,
          label: field.name || field.systemKey,
        })),
    ],
    [envelopeFields, t]
  );

  const completedCount = useMemo(
    () => Object.values(rows).filter(({ status }) => status === DownloadStatus.DONE).length,
    [rows]
  );

  const totalCount = Object.keys(rows).length;
  const isContinuationAvailable = continuationStates.length > 0;
  const isExportStarted = totalCount > 0;

  const setRowState = (key: string, row: IDownloadRow): void => {
    setRows((prev) => ({
      ...prev,
      [key]: row,
    }));
  };

  const getArchiveRowTitle = (
    direction: EnvelopeDirection,
    jobIndex: number,
    envelopeCount?: number
  ): string => {
    const title = isAllEnvelopesSelected
      ? t('envelopes:massDownload.archiveAllMatching')
      : t('envelopes:massDownload.archive', {
        count: envelopeCount ?? selectedEnvelopes.length,
      });
    const directionTitle = t(`envelopes:${direction.toLowerCase()}`);

    return `${title} — ${directionTitle}${jobIndex > 1 ? ` ${jobIndex}` : ''}`;
  };

  const getZipRowKey = (direction: EnvelopeDirection, jobIndex: number): string =>
    `${ZIP_ROW_KEY}-${direction.toLowerCase()}-${jobIndex}`;

  const getZipFallbackFileName = (direction: EnvelopeDirection, jobIndex: number): string =>
    `envelopes-${direction.toLowerCase()}${jobIndex > 1 ? `-${jobIndex}` : ''}.zip`;

  const getInitialExportJobParams = (direction: EnvelopeDirection): IParams => ({
    ...filterParams,
    boxOguid,
    direction,
  });

  const getInitialExportJobRequest = (
    target: IEnvelopeExportTarget,
    nameRulesTemplate: string
  ): IEnvelopeZipExportJobPostRequest => ({
    mode: EnvelopeZipExportMode.BASE,
    namePrefixMode: EnvelopeZipExportNamePrefixMode.NONE,
    nameRulesTemplate,
    ...(target.envelopeOguids ? { envelopeOguids: target.envelopeOguids } : {}),
  });

  const getShouldCreateContinuationJob = (job: IEnvelopeZipExportJobResponse): boolean =>
    job.hasNext === true;

  const handleMaskChange = (evt: ChangeEvent<HTMLInputElement>): void => {
    setFileNameMask(evt.target.value);
  };

  const handleTokenInsert = (key: string) => (): void => {
    const token = `{%${key}%}`;
    const input = maskInputRef.current;
    const selectionStart = input?.selectionStart ?? fileNameMask.length;
    const selectionEnd = input?.selectionEnd ?? selectionStart;
    const left = fileNameMask.slice(0, selectionStart);
    const right = fileNameMask.slice(selectionEnd);
    const prefix = left.trim() && !/[_\s-]$/.test(left) ? '_' : '';
    const suffix = right.trim() && !/^[_\s-]/.test(right) ? '_' : '';
    const insertValue = `${prefix}${token}${suffix}`;
    const nextValue = `${left}${insertValue}${right}`;
    const nextCursorPosition = selectionStart + prefix.length + token.length;

    setFileNameMask(nextValue);
    setIsTokenBuilderOpen(false);

    window.requestAnimationFrame(() => {
      maskInputRef.current?.focus();
      maskInputRef.current?.setSelectionRange(nextCursorPosition, nextCursorPosition);
    });
  };

  const saveBlob = async (blob: Blob, fallbackFileName: string, contentDisposition?: string): Promise<void> => {
    const fileName = getContentDispositionFileName(contentDisposition) ?? fallbackFileName;
    const url = URL.createObjectURL(blob);
    const link = window.document.createElement('a');

    link.href = url;
    link.download = fileName;
    window.document.body.appendChild(link);
    link.click();
    window.document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const updateJobRow = (
    job: IEnvelopeZipExportJobResponse,
    rowKey: string,
    direction: EnvelopeDirection,
    jobIndex: number,
    envelopeCount?: number
  ): void => {
    setRowState(rowKey, {
      fileName: getJobProgressLabel(
        job,
        t(`envelopes:massDownload.jobStatus.${job.status}`, {
          defaultValue: t('envelopes:massActions.status.processing'),
        })
      ),
      status: job.status === EnvelopeZipExportJobStatus.COMPLETED
        ? DownloadStatus.DONE
        : DownloadStatus.PROCESSING,
      title: getArchiveRowTitle(direction, jobIndex, envelopeCount),
    });
  };

  const waitForCompletedJob = async (
    initialJob: IEnvelopeZipExportJobResponse,
    rowKey: string,
    direction: EnvelopeDirection,
    jobIndex: number,
    envelopeCount?: number
  ): Promise<IEnvelopeZipExportJobResponse> => {
    let job = initialJob;
    updateJobRow(job, rowKey, direction, jobIndex, envelopeCount);

    for (let attempt = 0; attempt < ZIP_POLL_ATTEMPTS; attempt += 1) {
      if (terminalStatuses.has(job.status)) return job;

      await sleep(ZIP_POLL_INTERVAL_MS);
      const response = await EnvelopesService.getExportZipJob(job.oguid);
      job = response.data;
      updateJobRow(job, rowKey, direction, jobIndex, envelopeCount);
    }

    throw new Error(t('envelopes:massDownload.timeout'));
  };

  const getTerminalJobError = (job: IEnvelopeZipExportJobResponse): string => {
    if (job.errorDescription) return job.errorDescription;
    if (job.status === EnvelopeZipExportJobStatus.EXPIRED) return t('envelopes:massDownload.expired');
    if (job.status === EnvelopeZipExportJobStatus.STOPPED) return t('envelopes:massDownload.stopped');

    return t('common:error');
  };

  const handleSubmit = async (): Promise<void> => {
    if (processing) return;

    const initialTargets = getEnvelopeExportTargets({
      currentDirection,
      isAllEnvelopesSelected,
      isMyTasks,
      selectedEnvelopes,
    });
    if (!continuationStates.length && !initialTargets.length) return;

    setProcessing(true);
    setGlobalError('');
    setProcessed(false);

    try {
      const nameRulesTemplate = normalizeNameRulesTemplate(fileNameMask, fieldKeys);
      const attempts: IExportAttempt[] = continuationStates.length
        ? continuationStates.map(({ direction, jobIndex, previousJobOguid }) => ({
          direction,
          jobIndex,
          params: {},
          request: { previousJobOguid } as IEnvelopeZipExportJobPostRequest,
        }))
        : initialTargets.map((target) => ({
          direction: target.direction,
          envelopeCount: target.envelopeOguids?.length,
          jobIndex: 1,
          params: getInitialExportJobParams(target.direction),
          request: getInitialExportJobRequest(target, nameRulesTemplate),
        }));
      const nextContinuationStates: IContinuationState[] = [];
      const errors: string[] = [];

      for (const attempt of attempts) {
        const { direction, envelopeCount, jobIndex, params, request } = attempt;
        const rowKey = getZipRowKey(direction, jobIndex);
        const title = getArchiveRowTitle(direction, jobIndex, envelopeCount);

        setRowState(rowKey, {
          fileName: t('envelopes:massDownload.preparing'),
          status: DownloadStatus.PROCESSING,
          title,
        });

        try {
          const response = await EnvelopesService.postExportZipJob(
            request,
            params,
            formatLocale
          );
          const job = await waitForCompletedJob(
            response.data,
            rowKey,
            direction,
            jobIndex,
            envelopeCount
          );

          if (job.status !== EnvelopeZipExportJobStatus.COMPLETED) {
            throw new Error(getTerminalJobError(job));
          }

          const fallbackFileName = getZipFallbackFileName(direction, jobIndex);
          const fileResponse = await EnvelopesService.getExportZipJobFile(job.oguid);
          const contentDisposition = getContentDispositionHeader(fileResponse.headers);
          await saveBlob(fileResponse.data, fallbackFileName, contentDisposition);

          setRowState(rowKey, {
            exportedEnvelopeCount: job.exportedEnvelopeCount,
            fileName: getContentDispositionFileName(contentDisposition) ?? fallbackFileName,
            status: DownloadStatus.DONE,
            title,
          });

          if (getShouldCreateContinuationJob(job)) {
            nextContinuationStates.push({
              direction,
              jobIndex: jobIndex + 1,
              previousJobOguid: job.oguid,
            });
          }
        } catch (err: any) {
          const error = getErrorMessage(err, t('common:error'));

          errors.push(error);
          setRowState(rowKey, {
            error,
            fileName: '-',
            status: DownloadStatus.ERROR,
            title,
          });
        }
      }

      setContinuationStates(nextContinuationStates);

      const notices = [...errors];
      if (nextContinuationStates.length) {
        notices.push(t('envelopes:massDownload.nextPortionAvailable'));
      }
      setGlobalError(notices.join('\n'));
    } finally {
      setProcessed(true);
      setProcessing(false);
    }
  };

  const handleClose = (): void => {
    if (processing) return;

    resetDialogState();
    dispatch(resetModal());
  };

  const renderRowResult = (row: IDownloadRow): ReactNode => {
    if (row.status === DownloadStatus.DONE) {
      return <IconCheck color='success-600' />;
    }

    if (row.status === DownloadStatus.PROCESSING) {
      return (
        <div className={styles.processingResult}>
          <Spinner />
        </div>
      );
    }

    if (row.status === DownloadStatus.ERROR) {
      return (
        <div className={styles.errorResult}>
          <IconWarning color='danger-500' />
          {row.error && <div>{row.error}</div>}
        </div>
      );
    }

    return t('envelopes:massActions.status.queued');
  };

  const renderRows = (): ReactNode => {
    if (!totalCount) return null;

    return (
      <div className={styles.documents}>
        <div className={styles.documentHead}>
          <div>{t('envelopes:massActions.document')}</div>
          <div>{t('envelopes:massDownload.file')}</div>
          <div>{t('envelopes:massActions.result')}</div>
        </div>
        {Object.entries(rows).map(([key, row]) => (
          <div className={styles.documentRow} key={key}>
            <div className={styles.documentTitle}>{row.title}</div>
            <div className={styles.fileName}>{row.fileName ?? '-'}</div>
            <div className={cn(styles.result, row.status === DownloadStatus.ERROR && 'text-danger')}>
              {renderRowResult(row)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <ModalWrapper
      id={Modal.ENVELOPE_MASS_DOWNLOAD}
      size={ILibModalSizes.MEDIUM}
      onCloseModal={handleClose}
      backdrop={false}
      disableHideFn={processing}
    >
      <header className={styles.header} id='myModalLabel'>
        <h4 className='m-0'>
          {t('envelopes:massDownload.title', {
            completed: completedCount,
            total: totalCount || 1,
          })}
        </h4>
      </header>
      <div className={styles.body}>
        <div>
          <FormLabel classes='font-xs'>
            {t('envelopes:massDownload.fileNameMask')}
          </FormLabel>
          <div className={styles.maskControl}>
            <Input
              id='mass_download_mask'
              name='mass_download_mask'
              value={fileNameMask}
              onChange={handleMaskChange}
              ref={maskInputRef}
              disabled={processing || isExportStarted}
              classes={{ input: styles.maskInput }}
            />
            <button
              type='button'
              className={styles.maskButton}
              onClick={() => setIsTokenBuilderOpen((prev) => !prev)}
              disabled={processing || isExportStarted}
              aria-expanded={isTokenBuilderOpen}
              aria-label={t('envelopes:massDownload.insertField')}
              title={t('envelopes:massDownload.insertField')}
            >
              <IconInput size='sm' />
            </button>
            {isTokenBuilderOpen && (
              <div className={styles.tokenBuilder}>
                {tokenOptions.map((option, index) => (
                  <button
                    type='button'
                    key={`${option.key}:${index}`}
                    className={styles.tokenButton}
                    onClick={handleTokenInsert(option.key)}
                  >
                    <span>{option.label}</span>
                    <code>{`{%${option.key}%}`}</code>
                  </button>
                ))}
              </div>
            )}
          </div>
          <p className={styles.hint}>{t('envelopes:massDownload.fileNameMaskHint')}</p>
        </div>
        {globalError && <div className={styles.notice}>{globalError}</div>}
        {renderRows()}
      </div>
      <footer className={styles.footer}>
        <Button
          theme='primary'
          onClick={handleSubmit}
          isDisabled={processing || (processed && !isContinuationAvailable)}
        >
          {isContinuationAvailable
            ? t('envelopes:massDownload.downloadNextPortion')
            : t('envelopes:massDownload.download')}
        </Button>
        <Button onClick={handleClose} externalClass='ml-4' isDisabled={processing}>
          {t('common:close')}
        </Button>
      </footer>
    </ModalWrapper>
  );
};
